'use client';
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { createClient } from '../lib/supabase/client';

export type UserRole = 'free' | 'premium' | 'business' | 'superadmin';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  questions_today: number;
  questions_reset_at: string;
}

interface AuthContextValue {
  user: any | null;
  profile: UserProfile | null;
  role: UserRole | null;
  loading: boolean;
  questionsToday: number;
  questionLimit: number;
  canAskQuestion: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, role?: UserRole) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

const QUESTION_LIMITS: Record<UserRole, number> = {
  free:       5,
  premium:    999999,
  business:   999999,
  superadmin: 999999,
};

// Create the supabase client once at module level so it is never recreated
// on re-renders or navigation, which would drop the persisted session.
const supabase = createClient();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser]       = useState<any | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string) => {
    if (!supabase) return;
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (data) setProfile(data as UserProfile);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id);
  }, [user, fetchProfile]);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setProfile(null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signIn = async (email: string, password: string) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, fullName: string, userRole: UserRole = 'free') => {
    if (!supabase) throw new Error('Supabase not configured');
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, role: userRole },
        emailRedirectTo: `${siteUrl}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const role = profile?.role ?? null;
  const questionsToday = profile?.questions_today ?? 0;
  const questionLimit = role ? QUESTION_LIMITS[role] : 3;
  const canAskQuestion = questionsToday < questionLimit;

  return (
    <AuthContext.Provider value={{
      user, profile, role, loading,
      questionsToday, questionLimit, canAskQuestion,
      signIn, signUp, signOut, refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
